import React, { useCallback } from "react";
import Select from "react-select";
import { API_BASE_URL } from "../../environment";
import { handleAuthError, getAuthHeaders } from "../../utils/authUtils";
import axios from "axios";
import columnNames from "../../assets/dictionary/columnNames.json";
import { currencyOptions } from "../currency";
import { languageOptions } from "../language";
import { timezoneOptions } from "../timezone";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { formatCustomDateTime } from "../../utils/dateUtils";
import {
  SalesActivityService,
  SalesActivityLog,
} from "../../feature-module/crm/salesActivity/salesActivity.service";
import { resolveActivityIcon } from "../IconPicker/activityIcons";

const API_PAGE_SIZE = 50;

const SLICE_INCREMENT = 10;

interface CreatedBy {
  user_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
}

interface ActivityType {
  activity_type_id: number;
  activity_type_key: string;
  activity_type_display_name: string;
}

interface RelatedEntity {
  related_entity_name: string;
  related_entity_id: number;
}

interface Tenant {
  tenant_id: number;
  tenant_name: string;
  domain_name: string;
  time_zone: string;
}

interface Activity {
  activity_id: number;
  activity_type_id: number;
  configuration: string;
  created_datetime: string;
  created_by: CreatedBy;
  tenant_id: number;
  activity_type: ActivityType;
  related_entity: RelatedEntity;
  tenant: Tenant;
  updated_activity?: Record<string, any>;
}

interface ActivityApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Activity[];
}

interface ActivitiesProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  refreshTrigger?: number;
  related_entity_name?: string | null;
  crm_entity_id?: number | string | null;
}

type FeedItem =
  | { kind: "activity"; data: Activity }
  | { kind: "sales"; data: SalesActivityLog };

const Activities: React.FC<ActivitiesProps> = ({
  tenant_id,
  related_entity_id,
  refreshTrigger,
  related_entity_name,
  crm_entity_id,
}) => {
  const [activities, setActivities] = React.useState<Activity[]>([]);
  const [salesActivities, setSalesActivities] = React.useState<
    SalesActivityLog[]
  >([]);
  const [displayCount, setDisplayCount] =
    React.useState<number>(SLICE_INCREMENT);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [nextUrl, setNextUrl] = React.useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = React.useState(false);

  const { isVisible, hasBeenActivated } = useTabVisibility({
    tabId: "activities",
    onTabActivated: () => {
      if (!dataLoaded && tenant_id && related_entity_id) {
        fetchActivities();
        fetchSalesActivities();
        setDataLoaded(true);
      }
    },
  });

  const fetchActivities = React.useCallback(
    async (url?: string) => {
      setLoading((prev) => (url ? prev : true));
      setError(null);

      try {
        const endpoint =
          url ??
          `${API_BASE_URL}/activity/?tenant_id=${tenant_id}&related_entity_id=${related_entity_id}&related_entity_name=${related_entity_name}&page_size=${API_PAGE_SIZE}`;

        const response = await axios.get<ActivityApiResponse>(endpoint, {
          headers: getAuthHeaders(),
        });

        setActivities((prev) =>
          url ? [...prev, ...response.data.results] : response.data.results
        );
        setNextUrl(response.data.next);
      } catch (err: any) {
        handleAuthError(err);
        setError("Error loading activities.");
        console.error("Error fetching activities:", err);
      } finally {
        setLoading(false);
      }
    },
    [tenant_id, related_entity_id]
  );

  const fetchSalesActivities = React.useCallback(async () => {
    if (!tenant_id || !related_entity_id || !crm_entity_id) {
      setSalesActivities([]);
      return;
    }

    try {
      const logs = await SalesActivityService.getActivityLogs(
        Number(tenant_id),
        Number(crm_entity_id),
        Number(related_entity_id)
      );
      setSalesActivities(logs);
    } catch (err) {
      console.error("Error fetching sales activity logs:", err);
    }
  }, [tenant_id, related_entity_id, crm_entity_id]);

  React.useEffect(() => {
    if (hasBeenActivated && !dataLoaded && tenant_id && related_entity_id) {
      fetchActivities();
      fetchSalesActivities();
      setDataLoaded(true);
    }
  }, [
    hasBeenActivated,
    dataLoaded,
    tenant_id,
    related_entity_id,
    fetchActivities,
    fetchSalesActivities,
  ]);

  React.useEffect(() => {
    if (dataLoaded && refreshTrigger && tenant_id && related_entity_id) {
      fetchActivities();
      fetchSalesActivities();
    }
  }, [
    refreshTrigger,
    dataLoaded,
    tenant_id,
    related_entity_id,
    fetchActivities,
    fetchSalesActivities,
  ]);

  const handleLoadMore = async () => {
    if (displayCount < activities.length) {
      setDisplayCount((dc) =>
        Math.min(dc + SLICE_INCREMENT, activities.length)
      );
      return;
    }

    if (nextUrl) {
      setLoadingMore(true);
      const prevLength = activities.length;
      await fetchActivities(nextUrl);
      setLoadingMore(false);

      setDisplayCount((dc) => {
        const newLength = activities.length;
        if (newLength > prevLength) {
          return Math.min(dc + SLICE_INCREMENT, newLength);
        }
        return newLength;
      });
    }
  };

  const getFieldDisplayName = (fieldKey: string): string => {
    return (columnNames as Record<string, string>)[fieldKey] || fieldKey;
  };

  const getCurrencyLabel = (value: string): string => {
    const currency = currencyOptions.find((o) => o.value === value);
    return currency ? currency.label : value;
  };

  const getLanguageLabel = (value: string): string => {
    const language = languageOptions.find((o) => o.value === value);
    return language ? language.label : value;
  };

  const getTimezoneLabel = (value: string): string => {
    const timezone = timezoneOptions.find((o) => o.value === value);
    return timezone ? timezone.label : value;
  };

  const getDisplayValue = (key: string, value: any): string => {
    const str = String(value);
    if (key.toLowerCase().includes("currency") || key === "currency_code") {
      return getCurrencyLabel(str);
    }
    if (
      key.toLowerCase().includes("language") ||
      key === "preferred_language"
    ) {
      return getLanguageLabel(str);
    }
    if (
      key.toLowerCase().includes("timezone") ||
      key.toLowerCase().includes("time_zone") ||
      key === "time_zone"
    ) {
      return getTimezoneLabel(str);
    }
    return str;
  };

  const isUrl = (str: string): boolean => {
    try {
      new URL(str.startsWith("http") ? str : `https://${str}`);
      return (
        str.includes(".") &&
        (str.startsWith("http") ||
          str.includes("www.") ||
          str.split(".").length > 1)
      );
    } catch {
      return false;
    }
  };

  const renderDisplayValue = (key: string, value: any) => {
    const displayValue = getDisplayValue(key, value);
    const isLongText = displayValue.length > 100;
    const truncatedValue = isLongText
      ? `${displayValue.substring(0, 100)}...`
      : displayValue;

    if (isUrl(displayValue)) {
      const url = displayValue.startsWith("http")
        ? displayValue
        : `https://${displayValue}`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={displayValue}
          className="text-decoration-none"
        >
          {truncatedValue}
        </a>
      );
    }

    if (isLongText) {
      return <span title={displayValue}>{truncatedValue}</span>;
    }

    return displayValue;
  };

  const getActivityIcon = (activityType: string) => {
    switch (activityType.toLowerCase()) {
      case "create":
        return { icon: "ti ti-check", color: "bg-success" };
      case "update":
        return { icon: "ti ti-edit", color: "bg-orange" };
      case "delete":
        return { icon: "ti ti-trash", color: "bg-danger" };
      case "note_create":
        return { icon: "ti ti-notes", color: "bg-success" };
      case "note_update":
        return { icon: "ti ti-notes", color: "bg-orange" };
      case "note_delete":
        return { icon: "ti ti-notes", color: "bg-danger" };
      case "file_create":
        return { icon: "fa-regular fa-file", color: "bg-success" };
      case "file_update":
        return { icon: "fa-regular fa-file", color: "bg-orange" };
      case "file_delete":
        return { icon: "fa-regular fa-file", color: "bg-danger" };
      case "task_create":
        return { icon: "fa-solid fa-list-check", color: "bg-success" };
      case "task_update":
        return { icon: "fa-solid fa-list-check", color: "bg-orange" };
      case "task_delete":
        return { icon: "fa-solid fa-list-check", color: "bg-danger" };
      case "lifecycle_product_n_service_create":
        return { icon: "fa-solid fa-cart-shopping", color: "bg-success" };
      case "lifecycle_product_n_service_update":
        return { icon: "fa-solid fa-cart-shopping", color: "bg-orange" };
      case "lifecycle_product_n_service_delete":
        return { icon: "fa-solid fa-cart-shopping", color: "bg-danger" };
      case "custom_field_value_create":
        return { icon: "ti ti-forms", color: "bg-success" };
      case "custom_field_value_update":
        return { icon: "ti ti-forms", color: "bg-orange" };
      case "custom_field_value_delete":
        return { icon: "ti ti-forms", color: "bg-danger" };
      case "email_sent":
        return { icon: "ti ti-mail", color: "bg-info" };
      default:
        return { icon: "ti ti-activity", color: "bg-primary" };
    }
  };

  const getActivityMessage = (
    activityType: string,
    relatedEntityName: string
  ) => {
    const capName =
      relatedEntityName.charAt(0).toUpperCase() + relatedEntityName.slice(1);
    const type = activityType.toUpperCase();

    // Handle lifecycle_product_n_service specific cases
    if (type.includes("LIFECYCLE_PRODUCT_N_SERVICE")) {
      if (type.includes("CREATE")) return "added Product/Service";
      if (type.includes("UPDATE")) return "updated Product/Service";
      if (type.includes("DELETE")) return "deleted Product/Service";
    }

    if (type.includes("NOTE")) {
      if (type.includes("CREATE")) return "created note";
      if (type.includes("UPDATE")) return "updated note";
      if (type.includes("DELETE")) return "deleted note";
    }

    if (type.includes("FILE")) {
      if (type.includes("CREATE")) return "created file";
      if (type.includes("UPDATE")) return "updated file";
      if (type.includes("DELETE")) return "deleted file";
    }

    if (type.includes("TASK")) {
      if (type.includes("CREATE")) return "created task";
      if (type.includes("UPDATE")) return "updated task";
      if (type.includes("DELETE")) return "deleted task";
    }

    if (type.includes("CUSTOM_FIELD_VALUE")) {
      if (type.includes("CREATE")) return "added custom fields";
      if (type.includes("UPDATE")) return "updated custom fields";
      if (type.includes("DELETE")) return "deleted custom fields";
    }

    if (type === "EMAIL_SENT") {
      return `sent an email to this ${capName}`;
    }

    switch (type) {
      case "CREATE":
        return `created this ${capName}`;
      case "UPDATE":
        return `updated this ${capName}`;
      case "DELETE":
        return `deleted this ${capName}`;
      default:
        return `${activityType.toLowerCase()}d this ${capName}`;
    }
  };

  const isDateOnly = (str: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}$/.test(str);
  };

  const isDateTime = (str: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}:\d{2}\s+(AM|PM)$/i.test(str) ||
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str);
  };

  const formatCustomFieldValue = (value: any): string => {
    if (value === null || value === undefined) return "Not Set";

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (typeof value === "object") {
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    }

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === "object") {
          if (parsed.start_time && parsed.end_time) {
            return `${parsed.start_time} - ${parsed.end_time}`;
          }
          if (parsed.start_date && parsed.end_date) {
            const formattedStart = formatCustomDateTime(parsed.start_date, true);
            const formattedEnd = formatCustomDateTime(parsed.end_date, true);
            return `${formattedStart} to ${formattedEnd}`;
          }
          return Object.entries(parsed)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ");
        }
      } catch {
        if (isDateTime(value)) {
          return formatCustomDateTime(value, false);
        }
        if (isDateOnly(value)) {
          return formatCustomDateTime(value, true);
        }
        return String(value);
      }

      if (isDateTime(value)) {
        return formatCustomDateTime(value, false);
      }
      if (isDateOnly(value)) {
        return formatCustomDateTime(value, true);
      }
    }

    return String(value);
  };

  const parseCustomFields = (configuration: string) => {
    try {
      const config = JSON.parse(configuration);
      return config.custom_fields || [];
    } catch {
      return [];
    }
  };

  const groupFeedByDate = (items: FeedItem[]) => {
    const grouped: Record<string, FeedItem[]> = {};
    items.forEach((item) => {
      const d = new Date(item.data.created_datetime).toDateString();
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(item);
    });
    return grouped;
  };

  const SALES_ACTIVITY_BADGE_COLORS = [
    "bg-info",
    "bg-success",
    "bg-primary",
    "bg-orange",
    "bg-danger",
  ];

  const getSalesActivityIcon = (
    log: SalesActivityLog["crm_sales_activity_type"]
  ) => {
    const icon = resolveActivityIcon(
      log.icon,
      log.crm_sales_activity_type_display_name
    );
    const color =
      SALES_ACTIVITY_BADGE_COLORS[
        log.crm_sales_activity_type_id % SALES_ACTIVITY_BADGE_COLORS.length
      ];
    return { icon, color };
  };

  if (loading) {
    return (
      <div className="tab-pane active show" id="activities">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="fw-semibold">Latest Activities</h4>
          </div>
          <div className="card-body">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading activities...</span>
                </div>
                <h6 className="text-muted">Loading activities...</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-pane active show" id="activities">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="fw-semibold">Latest Activities</h4>
          </div>
          <div className="card-body">
            <div className="text-center py-4">
              <div className="alert alert-danger">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderActivityItem = (activity: Activity) => {
    const { icon, color } = getActivityIcon(
      activity.activity_type.activity_type_key
    );

    let emailConfig: any = null;
    if (
      activity.activity_type.activity_type_key === "EMAIL_SENT" &&
      activity.configuration
    ) {
      try {
        emailConfig = JSON.parse(activity.configuration);
      } catch (e) { }
    }

    const name = activity.related_entity.related_entity_name;
    const time = new Date(activity.created_datetime).toLocaleTimeString(
      "en-US",
      {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }
    );

    return (
      <div
        key={activity.activity_id}
        className="card border shadow-none mb-3"
      >
        <div className="card-body p-3">
          <div className="d-flex">
            <span
              className={`avatar avatar-md flex-shrink-0 rounded me-2 ${color}`}
            >
              <i className={icon} />
            </span>
            <div>
              <p className="mb-1">
                {activity.created_by.full_name}{" "}
                {getActivityMessage(
                  activity.activity_type.activity_type_key,
                  name
                )}
                {emailConfig && emailConfig.mail_message_id && (
                  <a
                    href={`/application/email/sent/${emailConfig.mail_message_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="badge bg-primary-transparent text-primary ms-2 p-1 px-2"
                    style={{
                      textDecoration: "none",
                      fontWeight: 500,
                      verticalAlign: "middle",
                      display: "inline-flex",
                      alignItems: "center"
                    }}
                  >
                    View Email <i className="ti ti-external-link ms-1"></i>
                  </a>
                )}
              </p>
              {(() => {
                const customFields = parseCustomFields(
                  activity.configuration
                );
                const isUpdate =
                  activity.activity_type.activity_type_key.toLowerCase() ===
                  "update" ||
                  activity.activity_type.activity_type_key
                    .toLowerCase()
                    .includes("custom_field_value");
                const hasCustomFields =
                  customFields.length > 0;
                const hasUpdatedActivity =
                  activity.updated_activity &&
                  Object.keys(activity.updated_activity)
                    .length > 0;

                if (isUpdate && hasCustomFields) {
                  return (
                    <h6 className="fw-medium mb-1">
                      {customFields.map(
                        (
                          field: {
                            custom_field_name: string;
                            custom_field_value: any;
                          },
                          idx: number
                        ) => (
                          <span key={idx}>
                            <strong>
                              {field.custom_field_name}:{" "}
                            </strong>
                            {formatCustomFieldValue(
                              field.custom_field_value
                            )}
                            {idx < customFields.length - 1 && (
                              <br />
                            )}
                          </span>
                        )
                      )}
                    </h6>
                  );
                }

                if (
                  isUpdate &&
                  !hasCustomFields &&
                  hasUpdatedActivity &&
                  activity.updated_activity
                ) {
                  return (
                    <h6 className="fw-medium mb-1">
                      {Object.entries(
                        activity.updated_activity
                      ).map(([key, value], idx, arr) => {
                        const label = getFieldDisplayName(key);
                        return (
                          <span key={key}>
                            <strong>{label}: </strong>
                            {renderDisplayValue(key, value)}
                            {idx < arr.length - 1 && <br />}
                          </span>
                        );
                      })}
                    </h6>
                  );
                }

                return null;
              })()}
              <p className="mb-0 text-muted" style={{ fontSize: "0.75rem" }}>{time}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSalesActivityItem = (log: SalesActivityLog) => {
    const { icon, color } = getSalesActivityIcon(log.crm_sales_activity_type);
    const actorName =
      typeof log.created_by === "object" && log.created_by
        ? log.created_by.full_name
        : "Someone";
    const time = new Date(log.created_datetime).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return (
      <div
        key={`sales-${log.crm_sales_activity_log_id}`}
        className="card border shadow-none mb-3"
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex">
              <span
                className={`avatar avatar-md flex-shrink-0 rounded me-2 ${color}`}
              >
                <i className={icon} />
              </span>
              <div>
                <p className="mb-1">
                  {actorName} logged a{" "}
                  <span className="fw-bold">
                    {
                      log.crm_sales_activity_type
                        .crm_sales_activity_type_display_name
                    }
                  </span>{" "}
                  activity →{" "}
                  <span className="fw-bold">
                    "
                    {
                      log.crm_sales_activity_type_status
                        .crm_sales_activity_type_status_display_name
                    }
                    "
                  </span>
                </p>
                {log.notes && <p className="mb-1 text-muted">{log.notes}</p>}
                <p className="mb-0 text-muted" style={{ fontSize: "0.75rem" }}>
                  {time}
                </p>
              </div>
            </div>
            <span
              className="badge ms-2 flex-shrink-0 fw-bold"
              style={{ backgroundColor: "#824EEB", color: "#fff", fontSize: "0.7rem", padding: "0.3rem 0.4rem", borderRadius: '10px' }}
            >
              Activity
            </span>
          </div>
        </div>
      </div>
    );
  };

  const visibleActivities = activities.slice(0, displayCount);
  const feedItems: FeedItem[] = [
    ...visibleActivities.map((a) => ({ kind: "activity" as const, data: a })),
    ...salesActivities.map((s) => ({ kind: "sales" as const, data: s })),
  ].sort(
    (a, b) =>
      new Date(b.data.created_datetime).getTime() -
      new Date(a.data.created_datetime).getTime()
  );
  const grouped = groupFeedByDate(feedItems);

  return (
    <div className="tab-pane active show" id="activities">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h4 className="fw-semibold">Latest Activities</h4>
        </div>

        <div className="card-body">
          {feedItems.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No activities found.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([date, items]) => (
              <div key={date}>
                <div className="badge badge-soft-purple fs-14 fw-normal shadow-none mb-3">
                  <i className="ti ti-calendar-check me-1" />
                  {new Date(date).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </div>

                {items.map((item) =>
                  item.kind === "sales"
                    ? renderSalesActivityItem(item.data)
                    : renderActivityItem(item.data)
                )}
              </div>
            ))
          )}

          {(displayCount < activities.length || nextUrl) && (
            <div className="text-center my-3">
              <button
                className="btn btn-outline-primary"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load More"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Activities;
